import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getPurityStandards } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Shield, Gem } from 'lucide-react';

export default function PurityStandardsPage() {
  const [standards, setStandards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPurityStandards();
        setStandards(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="purity-standards-page">
      <Navbar />
      <div className="grain-overlay" />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">BIS Purity Standards</h1>
          </div>
          <p className="text-muted-foreground mb-2">Bureau of Indian Standards - Government-certified metal purity grades</p>
          <p className="text-xs text-muted-foreground mb-8">IS 1417 (Gold) | IS 2112 (Silver)</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : standards && (
          <Tabs defaultValue="gold">
            <TabsList className="bg-secondary/50 border border-border/40 mb-8">
              <TabsTrigger value="gold" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold" data-testid="tab-gold">Gold (IS 1417)</TabsTrigger>
              <TabsTrigger value="silver" className="data-[state=active]:bg-silver data-[state=active]:text-black font-bold" data-testid="tab-silver">Silver (IS 2112)</TabsTrigger>
            </TabsList>

            <TabsContent value="gold">
              <div className="space-y-4">
                {standards.gold.map((g, i) => (
                  <motion.div
                    key={g.bis_code}
                    className="bg-card border border-border/40 hover:border-primary/30 rounded-sm p-5 transition-colors"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    data-testid={`gold-purity-${g.bis_code}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-display text-2xl font-bold text-primary">{g.karat}</span>
                          <Badge variant="outline" className="border-primary/50 text-primary text-xs rounded-sm">BIS {g.bis_code}</Badge>
                          <Badge variant="outline" className="border-white/20 text-muted-foreground text-xs rounded-sm">{g.purity_percent}% Pure</Badge>
                        </div>
                        <h3 className="font-display font-bold mb-1">{g.label}</h3>
                        <p className="text-sm text-muted-foreground">{g.description}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="font-mono text-xl font-bold text-primary">{g.fineness}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Fineness</p>
                      </div>
                    </div>
                    {/* Purity bar */}
                    <div className="mt-4">
                      <div className="w-full h-2 bg-muted rounded-full">
                        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${g.purity_percent}%` }} />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0%</span>
                        <span>{g.purity_percent}% Gold</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="silver">
              <div className="space-y-4">
                {standards.silver.map((s, i) => (
                  <motion.div
                    key={s.bis_code}
                    className="bg-card border border-border/40 hover:border-silver/30 rounded-sm p-5 transition-colors"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    data-testid={`silver-purity-${s.bis_code}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-display text-2xl font-bold text-silver">{s.grade}</span>
                          <Badge variant="outline" className="border-silver/50 text-silver text-xs rounded-sm">BIS {s.bis_code}</Badge>
                          <Badge variant="outline" className="border-white/20 text-muted-foreground text-xs rounded-sm">{s.purity_percent}% Pure</Badge>
                        </div>
                        <h3 className="font-display font-bold mb-1">{s.label}</h3>
                        <p className="text-sm text-muted-foreground">{s.description}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="w-16 h-16 bg-silver/10 rounded-full flex items-center justify-center">
                          <span className="font-mono text-xl font-bold text-silver">{s.fineness}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Fineness</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="w-full h-2 bg-muted rounded-full">
                        <div className="h-full bg-silver rounded-full transition-all" style={{ width: `${s.purity_percent}%` }} />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0%</span>
                        <span>{s.purity_percent}% Silver</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* BIS Hallmark Info */}
        <motion.div className="mt-12 bg-card border border-primary/20 rounded-sm p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <div className="flex items-center gap-3 mb-4">
            <Gem className="w-5 h-5 text-primary" />
            <h2 className="font-display text-xl font-bold">What is BIS Hallmarking?</h2>
          </div>
          <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">
            <p>BIS hallmarking is mandatory for gold jewelry in India since June 2021. It guarantees the purity of precious metals.</p>
            <p><strong className="text-foreground">The hallmark includes:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>BIS Logo - confirms testing at accredited center</li>
              <li>Purity/Fineness Grade (999, 916, 750, etc.)</li>
              <li>HUID - 6-digit Hallmark Unique Identification number</li>
              <li>Assaying & Hallmarking Center identification</li>
            </ul>
            <p>All SŌHM products carry genuine BIS hallmarks that can be verified online through the BIS CARE app.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
